import { useEffect, useRef, useState } from 'react';

import {
    type FetchHistoryResult,
    type ProviderConfigError,
    parseProviderConfigError,
    useFetchHistory,
} from '@/domain/providerConfig';
import { serverErrorView } from '@/routes/shared/ProviderConfig/form/serverErrors';

import { chunkWindow, type DayWindow } from '../components/fetchHistoryChunks';

/**
 * Days per request. The server takes up to 120; the ingress timeout in front of it is not
 * known yet, so a chunk stays well under it.
 */
export const HISTORY_CHUNK_DAYS = 90;
/** The server lets one user fetch history once per 5 seconds. */
export const HISTORY_CHUNK_PAUSE_MS = 5_500;

export interface HistoryRun {
    phase: 'idle' | 'running' | 'done' | 'failed';
    total: number;
    /** The chunk being fetched, or the one that failed. */
    current: DayWindow | null;
    done: number;
    written: number;
    providerName: string | null;
    failedDays: number;
    failureKey: string | null;
}

const IDLE: HistoryRun = {
    phase: 'idle',
    total: 0,
    current: null,
    done: 0,
    written: 0,
    providerName: null,
    failedDays: 0,
    failureKey: null,
};

/**
 * Copy for a failed chunk. The window is checked before the first request, so a request
 * validation 400 here can only be a day the server reads differently.
 */
export function historyFailureKey(error: ProviderConfigError): string {
    if (error.kind === 'badRequest') return 'providerConfig.errors.invalidWindow';
    const view = serverErrorView(error);
    return view.fields[0]?.messageKey ?? view.formMessageKey ?? 'providerConfig.errors.generic';
}

function pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * When this page last sent a history request. Module-wide, as the server's cooldown is per
 * user: a run started right after another one, in a dialog opened again, waits it out too.
 */
let lastRequestAt = 0;

/** Fetches a pen's history chunk by chunk, one request after another, stopping at a failure. */
export function useHistoryRun(
    locationId: number,
    pauseMs: number
): { state: HistoryRun; start: (cageId: number, window: DayWindow) => Promise<void> } {
    const { run } = useFetchHistory(locationId);
    const [state, setState] = useState<HistoryRun>(IDLE);
    const active = useRef(true);
    useEffect(() => {
        active.current = true;
        return () => {
            active.current = false;
        };
    }, []);

    async function start(cageId: number, window: DayWindow): Promise<void> {
        const chunks = chunkWindow(window.from, window.to, HISTORY_CHUNK_DAYS);
        let progress: HistoryRun = { ...IDLE, phase: 'running', total: chunks.length };
        for (const [index, chunk] of chunks.entries()) {
            if (!active.current) return;
            progress = { ...progress, current: chunk };
            setState(progress);
            const wait = lastRequestAt + pauseMs - Date.now();
            if (wait > 0) await pause(wait);
            // Closed during the wait: the next chunk must not go out.
            if (!active.current) return;
            lastRequestAt = Date.now();
            let result: FetchHistoryResult;
            try {
                result = await run({ cageId, from: chunk.from, to: chunk.to });
            } catch (failure) {
                const failureKey = historyFailureKey(parseProviderConfigError(failure));
                if (active.current) setState({ ...progress, phase: 'failed', failureKey });
                return;
            }
            progress = {
                ...progress,
                done: index + 1,
                written: progress.written + result.written,
                providerName: result.providerName ?? progress.providerName,
                failedDays: progress.failedDays + result.failedDays.length,
            };
        }
        if (active.current) setState({ ...progress, phase: 'done', current: null });
    }

    return { state, start };
}
